<template>
    <div class="q-pa-none">
        <div id="flexContainer" ref="flexContainer" class="row wrap items-start" :style="{
            overflowY: (expanded ? 'visible' : 'hidden'),
            maxHeight: (expanded ? '' : '70px'),
            minHeight: '35px'
        }">
            <span class="pill-group-label q-mr-sm consistentText" style="white-space: nowrap;">
                {{ Translate('Selected') }} {{ typeDescriptor }}:
            </span>

            <draggable 
                v-model="internalPills" 
                class="row wrap items-start" 
                style="flex: 1; gap: 6px; padding-top: 6px;"
                :animation="200"
                :item-key="getItemKey"
            >
                <template #item="{ element: item }">
                    <q-chip
                        removable
                        rounded
                        outline
                        color="primary"
                        text-color="primary"
                        class="pill q-ma-none"
                        remove-icon="mdi-close"
                        style="cursor: grab;"
                        @remove="removePill(item)"
                    >
                        <template v-if="item.calculation && item.calculation !== 'bucket'">
                            {{ formatHeader({ type: item.SourceType, headerName: item.Title, aggFunc: item.calculation }) }}
                        </template>
                        <template v-else>
                            {{ item.headerName || Translate(item.Name || item.Title) }}
                        </template>
                    </q-chip>
                </template>
            </draggable>
        </div>
        
        <div role="button" v-if="expanded" @click="toggleExpansion()" class="self-start q-ml-sm q-mt-sm consistentText cursor-pointer" style="text-decoration: underline; white-space: nowrap;">
            {{ Translate('Hide') }}
        </div>
        <div role="button" class="consistentText cursor-pointer" style="text-decoration: underline;" v-if="hasMorePills && !expanded" @click="toggleExpansion()">
            {{ Translate('Show all') }}
        </div>
    </div>
</template>

<script>
import { nextTick } from 'vue';
import common from './common.js';
import methods from '@/Shared/methods';
import { QChip } from 'quasar';
import draggable from 'vuedraggable';

export default {
    components: { QChip, draggable },
    props: {
        removePill: null,
        pills: Array,
        typeDescriptor: null
    },
    data() {
        return {
            expanded: false,
            hasMorePills: false,
            formatHeader: common.formatHeader,
        };
    },
    computed: {
        // Vue 3 vuedraggable works best with a computed v-model that maps directly to your emit
        internalPills: {
            get() {
                return this.pills;
            },
            set(value) {
                this.$emit('reorder', value);
            }
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(() => {
            this.checkOverflow();
        });

        nextTick(() => {
            if (this.$refs.flexContainer) {
                this.resizeObserver.observe(this.$refs.flexContainer);
            }
        });
    },
    unmounted() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    watch: {
        pills: {
            handler() {
                this.checkOverflow();
            },
            deep: true,
        }
    },
    methods: {
        ...methods,
        checkOverflow() {
            const container = this.$refs.flexContainer;
            if (!container) return;
            if (container.clientHeight === 0) return;
            if (this.expanded) return;
            const scrollHeight = container.scrollHeight;
            const clientHeight = container.clientHeight;
            this.hasMorePills = scrollHeight > clientHeight;
        },
        toggleExpansion() {
            this.expanded = !this.expanded;
            nextTick(() => {
                this.checkOverflow();
            });
        },
        getItemKey(item) {
            return item.id || item.ColId || item.colId || item.headerName;
        }
    },
};
</script>

<style scoped>
.pill-group-label {
    align-self: flex-start !important;
    padding-top: 7px;
}
.pill {
    border: 1px solid var(--q-primary) !important;
    background-color: color-mix(in srgb, var(--q-primary) 15%, transparent) !important;
}
.pill::before {
    display: none;
}
.consistentText {
    color: var(--v-black-base) !important; 
    opacity: 0.8 !important; 
    font-size: 14px !important; 
    text-rendering: optimizelegibility !important; 
    letter-spacing:0.1 !important;
}
</style>